import { ArticleCard } from "@components/cards/ArticleCard"
import { ChipGroup } from "@components/controls/ChipGroup"
import { RichSelect } from "@components/controls/RichSelect"
import { SearchBox } from "@components/controls/SearchBox"
import { DirectoryHeader } from "@components/layout/DirectoryHeader"
import { ResultList, ResultListMode } from "@components/layout/ResultList"
import { StaggerTransitionRoot } from "@components/motion/Transition"
import { useInfiniteArticleSearch } from "@hooks/queries"
import { useSearchParams } from "eddev/routing"
import { defineView } from "eddev/views"
import { useCallback } from "react"

export const meta: ViewMeta = {
  templateName: "Discover Listing",
}

/**
 * Event listing page
 */
export default defineView("template-discover", (props) => {
  const config = props.page?.discoverConfig
  let allBranches = props.branches!.nodes.filter((b) => b.branchSettings?.branchFlags?.includes("canSearch"))
  let allCategories = props.articleCategories!.nodes
  const allowedCollections = config?.enableCollections?.map((c) => c?.slug)
  let allCollections = props.collections!.nodes.filter((s) => allowedCollections?.includes(s.slug!))

  const [params, filter] = useSearchParams({
    branch: "",
    category: [] as string[],
    collection: [] as string[],
    display: "card" as ResultListMode,
    search: "",
  })

  const lookup = useInfiniteArticleSearch({
    limit: 24,
    search: params.search,
    branch: params.branch ? [params.branch] : [],
    collection: params.collection.length === 0 ? allowedCollections : params.collection.filter((c) => allowedCollections?.includes(c)),
    category: params.category,
  })

  return (
    <StaggerTransitionRoot>
      <DirectoryHeader
        heading={props.page?.discoverConfig?.titleBar}
        displayMode={params.display}
        onDisplayModeChange={(value) => {
          document.scrollingElement?.scrollTo({ top: 0, behavior: "instant" })
          filter.set("display", value)
        }}
        tags={
          <div className="stack-x-1">
            {config?.enableCollections?.length! > 1 && (
              <ChipGroup
                value={filter.get("collection")}
                onValueChange={(value) => {
                  document.scrollingElement?.scrollTo({ top: 0, behavior: "instant" })
                  filter.set("collection", value)
                }}
                options={allCollections.map((b) => ({
                  value: b.slug!,
                  label: b.collectionSettings?.filterLabel || b.name!,
                }))}
              />
            )}
            {config?.enableBranchFilter && (
              <RichSelect
                variant="outline"
                size="chip"
                placeholder="Location"
                value={params.branch ?? ""}
                onChange={(v) => {
                  if (v === "all") {
                    filter.set("branch", "")
                  } else {
                    filter.set("branch", v)
                  }
                }}
                active={!!params.branch}
                options={[{ value: "all", label: "All" }, ...allBranches.map((b) => ({ value: b.slug!, label: b.shortName! }))]}
              />
            )}
          </div>
        }
        search={!!props.page?.discoverConfig?.enableSearch && <SearchBox onSearch={filter.setter("search")} placeholder="Search articles..." />}
      />
      <ResultList
        mode={filter.get("display")}
        items={lookup.data?.nodes}
        hasNextPage={lookup.hasNextPage}
        loading={lookup.isLoading}
        loadingNextPage={lookup.isFetchingNextPage}
        onFetchMore={lookup.fetchNextPage}
        component={ArticleCard}
        dynamicSizing={!!config?.dynamicSizing ? "totalDependant" : false}
        keyField="uri"
        mobileMode="row"
      />
    </StaggerTransitionRoot>
  )
})
